import { useEffect, useState, useRef } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Ban, Search, Printer, CheckCircle2, AlertTriangle, ShieldCheck, Loader2, FileWarning, Undo2, Camera, UserSquare2, Trash2 } from "lucide-react";

const inp = "w-full bg-[#0d1a30] border border-[#1e3a5f] px-2.5 py-1.5 text-xs outline-none focus:border-[#22D3EE] font-mono text-white rounded-sm";
const lbl = "font-mono text-[9px] text-[#7d9bc1] uppercase mb-0.5 block";
const panel = "border border-[#1e3a5f] bg-[#0d1a30] rounded-sm p-3";
const ptitle = "font-head text-[10px] font-bold tracking-wider text-[#22D3EE] mb-2";
const STATUS_COLOR = { active: "#10B981", approved_for_service: "#22D3EE", pending_supervisor_review: "#F59E0B", pending_owner_authorization: "#F59E0B", draft: "#9CA3AF", service_attempted: "#F97316", revoked: "#EF4444", temporarily_suspended: "#F59E0B", violation_documented: "#EF4444", expired: "#6B7280", superseded: "#6B7280", archived: "#6B7280", served: "#10B981", appealed_disputed: "#F59E0B" };
const fmt = (s) => (s || "").replace(/_/g, " ").toUpperCase();

const err = (e, msg) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : msg);

export default function ExclusionNotices() {
  const { user } = useAuth();
  const [tab, setTab] = useState("new");
  const [meta, setMeta] = useState(null);
  const [list, setList] = useState([]);
  const [dash, setDash] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const isSup = ["supervisor", "watch_commander", "records_supervisor", "admin"].includes(user?.role);

  useEffect(() => { client.get("/ban-notices/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const loadList = () => client.get("/ban-notices", { params: { q } }).then((r) => setList(r.data.notices)).catch(() => {});
  const loadDash = () => client.get("/ban-notices/dashboard").then((r) => setDash(r.data)).catch(() => {});
  useEffect(() => { if (tab === "list") loadList(); if (tab === "dash") loadDash(); }, [tab, q]); // eslint-disable-line

  return (
    <Layout>
      <PageHeader icon={Ban} title="TRESPASS & EXCLUSION NOTICES" subtitle="OWNER/AGENT EXCLUSION RECORDS · 6-STEP WORKFLOW · SUPERVISOR ROUTING · IMMUTABLE AFTER SERVICE" />
      <div className="px-4 pt-3 flex gap-1.5">
        {[["new", "NEW NOTICE"], ["list", "NOTICES"], ["import", "IMPORT LEGACY"], ["dash", "DASHBOARD"]].map(([k, l]) => (
          <button key={k} data-testid={`ban-tab-${k}`} onClick={() => { setTab(k); setSel(null); }}
            className={`px-4 py-1.5 text-[11px] font-head font-bold rounded-sm ${tab === k ? "bg-[#22D3EE] text-black" : "border border-[#1e3a5f] text-[#7d9bc1]"}`}>{l}</button>
        ))}
      </div>
      <div className="p-4">
        {tab === "new" && meta && <NoticeWizard meta={meta} onDone={() => setTab("list")} />}
        {tab === "import" && <LegacyImport onDone={() => setTab("list")} />}
        {tab === "list" && (sel
          ? <NoticeDetail nid={sel} isSup={isSup} onBack={() => { setSel(null); loadList(); }} />
          : (
            <div>
              <div className="relative mb-3 max-w-md">
                <Search size={13} className="absolute left-2.5 top-2 text-[#7d9bc1]" />
                <input data-testid="ban-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, notice #, property, CAD #…" className={inp + " pl-8"} />
              </div>
              <div className="space-y-1.5" data-testid="ban-list">
                {list.map((n) => (
                  <button key={n.id} data-testid={`ban-row-${n.id}`} onClick={() => setSel(n.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 border border-[#1e3a5f] bg-[#0d1a30] rounded-sm text-left hover:border-[#22D3EE]">
                    <BanRowThumb nid={n.id} photo={n.photo} />
                    <span className="font-mono text-[11px] text-[#22D3EE] w-32 shrink-0">{n.notice_number || "DRAFT"}</span>
                    <span className="text-sm text-white font-semibold">{n.subject?.name}</span>
                    <span className="font-mono text-[10px] text-[#7d9bc1] truncate">{n.property?.name} · {n.property?.address}</span>
                    <span className="ml-auto font-mono text-[9px] font-bold px-2 py-0.5 border rounded-sm shrink-0" style={{ color: STATUS_COLOR[n.status], borderColor: `${STATUS_COLOR[n.status]}55` }}>{fmt(n.status)}</span>
                  </button>
                ))}
                {!list.length && <div className="font-mono text-xs text-[#7d9bc1]">No exclusion notices on file.</div>}
              </div>
            </div>
          ))}
        {tab === "dash" && dash && (
          <div data-testid="ban-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
              {[["Issued today", dash.issued_today], ["Issued this month", dash.issued_month], ["Active", dash.active, "#10B981"],
                ["Pending service", dash.pending_service, "#22D3EE"], ["Failed attempts", dash.failed_attempts, "#F97316"],
                ["Pending review", dash.pending_review, "#F59E0B"], ["Indefinite awaiting review", dash.indefinite_awaiting_review, "#F59E0B"],
                ["Revocations", dash.revocations], ["Violations", dash.violations, "#EF4444"], ["Appeals/disputes", dash.disputed],
                ["Missing owner auth", dash.missing_owner_authorization, "#EF4444"], ["Involving residents/tenants", dash.involving_residents, "#F59E0B"]].map(([l, v, c]) => (
                <div key={l} className={panel}><div className="font-mono text-[9px] text-[#7d9bc1] uppercase">{l}</div>
                  <div className="font-head text-2xl font-bold" style={{ color: c || "#fff" }}>{v}</div></div>
              ))}
            </div>
            <div className={panel + " mt-3 max-w-md"}>
              <div className={ptitle}>NOTICES BY PROPERTY</div>
              {dash.by_property.map(([p, n]) => <div key={p} className="flex justify-between font-mono text-[11px] text-white py-0.5"><span>{p}</span><b>{n}</b></div>)}
            </div>
          </div>
        )}
      </div>
      <div className="px-4 pb-4 font-mono text-[10px] text-center" style={{ color: "#F59E0B" }}>
        OWNER/AGENT EXCLUSION RECORD — NOT A COURT ORDER OR ARREST WARRANT
      </div>
    </Layout>
  );
}

function LegacyImport({ onDone }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!text.trim() && !file) return toast.error("Upload a document or paste the notice text.");
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("text", text);
      const r = await client.post("/ban-notices/import", fd);
      setResult(r.data);
      toast.success(`Legacy notice ${r.data.notice_number} imported`);
    } catch (e) { err(e, "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="ban-import" className="max-w-2xl space-y-3">
      <div className={panel}>
        <div className={ptitle}>IMPORT PREVIOUS BAN / EXCLUSION RECORD (AI EXTRACTION — CJIS PLAIN LANGUAGE)</div>
        <div className="font-mono text-[10px] text-[#7d9bc1] mb-2">Upload a photo, PDF or text of an old paper ban notice. AI extracts the subject, property and dates, and writes a plain-language NENA/APCO/CJIS-compliant summary for easy review. Records import as ARCHIVED and appear in person-check and NCIC review.</div>
        <input data-testid="ban-import-file" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} className="font-mono text-[10px] text-[#7d9bc1] mb-2" />
        <textarea data-testid="ban-import-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="…or paste the legacy notice text here" className={inp} />
        <button data-testid="ban-import-run" onClick={run} disabled={busy} className="mt-2 px-4 py-2 text-[11px] font-head font-bold bg-[#8B5CF6] text-white rounded-sm flex items-center gap-1.5">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} EXTRACT & IMPORT
        </button>
      </div>
      {result && (
        <div className={panel} data-testid="ban-import-result">
          <div className={ptitle}>{result.notice_number} — {result.subject.name} (ARCHIVED LEGACY RECORD)</div>
          <div className="font-mono text-[11px] text-white mb-1">Property: {result.property.name} {result.property.address} · Expires: {result.expires || "—"}</div>
          <div className="font-mono text-[10px] text-[#7d9bc1] uppercase mb-0.5">CJIS Plain-Language Summary</div>
          <div className="text-sm text-[#C9CDD4] leading-relaxed">{result.cjis_summary}</div>
          <button onClick={onDone} className="mt-2 px-3 py-1.5 text-[10px] font-head border border-[#22D3EE] text-[#22D3EE] rounded-sm">VIEW IN NOTICES LIST</button>
        </div>
      )}
    </div>
  );
}

function NoticeWizard({ meta, onDone }) {
  const [f, setF] = useState({ incident: {}, subject: { aliases: [] }, property: {}, triggers: {}, basis: "", expires: "", exceptions: "", duration: "", owner_authorized: false });
  const [check, setCheck] = useState(null);
  const [validation, setValidation] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const setI = (sec, k) => (e) => setF((p) => ({ ...p, [sec]: { ...p[sec], [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value } }));
  const setT = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const personCheck = async () => {
    if (!f.subject.name) return;
    try { const r = await client.get("/ban-notices/person-check", { params: { name: f.subject.name } }); setCheck(r.data); }
    catch { /* noop */ }
  };
  const saveDraft = async () => {
    setBusy(true);
    try {
      const r = saved ? { data: saved } : await client.post("/ban-notices", f);
      if (!saved) { setSaved(r.data); toast.success("Draft saved"); }
      return r.data;
    } catch (e) { err(e, "Save failed"); return null; }
    finally { setBusy(false); }
  };
  const validate = async () => {
    const d = await saveDraft(); if (!d) return;
    try { const r = await client.post(`/ban-notices/${d.id}/validate`); setValidation(r.data); }
    catch (e) { err(e, "Validation failed"); }
  };
  const generate = async () => {
    const d = await saveDraft(); if (!d) return;
    try {
      const r = await client.post(`/ban-notices/${d.id}/generate`);
      toast.success(`Notice ${r.data.notice_number} generated — ${fmt(r.data.status)}`);
      onDone();
    } catch (e) { err(e, "Generate failed"); }
  };

  const step = (n, t) => <div className="flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-[#22D3EE] text-black font-head text-[10px] font-bold flex items-center justify-center">{n}</span><span className="font-head text-[10px] font-bold text-white">{t}</span></div>;

  return (
    <div data-testid="ban-wizard" className="grid lg:grid-cols-3 gap-3">
      <div className="lg:col-span-2 space-y-3">
        <div className={panel}>
          <div className="flex items-center justify-between mb-2">{step(1, "INCIDENT CONNECTION")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div><span className={lbl}>CAD event number *</span><input data-testid="ban-cad" value={f.incident.cad_number || ""} onChange={setI("incident", "cad_number")} className={inp} /></div>
            <div><span className={lbl}>Original call type *</span>
              <select data-testid="ban-calltype" value={f.incident.call_type || ""} onChange={setI("incident", "call_type")} className={inp}>
                <option value="">Select…</option>{meta.call_types.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><span className={lbl}>Date & time *</span><input type="datetime-local" value={f.incident.datetime || ""} onChange={setI("incident", "datetime")} className={inp} /></div>
            <div><span className={lbl}>Property / location *</span><input value={f.incident.location || ""} onChange={setI("incident", "location")} className={inp} /></div>
            <div><span className={lbl}>Reporting person *</span><input value={f.incident.reporting_person || ""} onChange={setI("incident", "reporting_person")} className={inp} /></div>
            <div><span className={lbl}>Responding unit *</span><input value={f.incident.responding_unit || ""} onChange={setI("incident", "responding_unit")} className={inp} /></div>
            <div><span className={lbl}>Incident disposition</span><input value={f.incident.disposition || ""} onChange={setI("incident", "disposition")} className={inp} /></div>
            <div><span className={lbl}>Related police report</span><input value={f.incident.police_report || ""} onChange={setI("incident", "police_report")} className={inp} /></div>
            <div><span className={lbl}>Related case numbers</span><input value={f.incident.case_numbers || ""} onChange={setI("incident", "case_numbers")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Associated prior incidents</span><input value={f.incident.prior_incidents || ""} onChange={setI("incident", "prior_incidents")} className={inp} /></div>
          </div>
        </div>

        <div className={panel}>
          <div className="flex items-center justify-between mb-2">{step(2, "SUBJECT INFORMATION & PERSON VERIFICATION")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="col-span-2"><span className={lbl}>Full name *</span>
              <div className="flex gap-1.5">
                <input data-testid="ban-subject-name" value={f.subject.name || ""} onChange={setI("subject", "name")} className={inp} />
                <button data-testid="ban-person-check" onClick={personCheck} className="px-2.5 bg-[#22D3EE] text-black rounded-sm"><Search size={13} /></button>
              </div></div>
            <div><span className={lbl}>DOB</span><input data-testid="ban-subject-dob" value={f.subject.dob || ""} onChange={setI("subject", "dob")} placeholder="YYYY-MM-DD" className={inp} /></div>
            <div><span className={lbl}>Sex / race</span><div className="flex gap-1"><input value={f.subject.sex || ""} onChange={setI("subject", "sex")} placeholder="M/F" className={inp} /><input value={f.subject.race || ""} onChange={setI("subject", "race")} className={inp} /></div></div>
            <div><span className={lbl}>Height / weight</span><div className="flex gap-1"><input value={f.subject.height || ""} onChange={setI("subject", "height")} className={inp} /><input value={f.subject.weight || ""} onChange={setI("subject", "weight")} className={inp} /></div></div>
            <div><span className={lbl}>ID / license #</span><input data-testid="ban-subject-id" value={f.subject.license_no || ""} onChange={setI("subject", "license_no")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Address</span><input value={f.subject.address || ""} onChange={setI("subject", "address")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Description / clothing</span><input value={f.subject.description || ""} onChange={setI("subject", "description")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Photo reference (where legally authorized)</span><input value={f.subject.photo_ref || ""} onChange={setI("subject", "photo_ref")} className={inp} /></div>
          </div>
          {check && (
            <div data-testid="ban-check-results" className="mt-2 border border-[#F59E0B]/40 bg-[#1a1408] p-2.5 rounded-sm">
              <div className="font-mono text-[10px] font-bold text-[#F59E0B] mb-1">{check.matches.length} PRIOR NOTICE(S) FOUND FOR "{check.query}"</div>
              {check.matches.map((m) => (
                <div key={m.id} className="font-mono text-[10px] text-[#C9CDD4]">{m.notice_number || "DRAFT"} · {m.subject.name} · {m.property_name} · {fmt(m.status)} · expires {m.expires || "—"} · {m.service_attempts} service attempt(s)</div>
              ))}
              <div className="font-mono text-[9px] text-[#F59E0B] mt-1 italic">{check.caution}</div>
            </div>
          )}
        </div>

        <div className={panel}>
          <div className="flex items-center justify-between mb-2">{step(3, "PROPERTY AUTHORITY — OWNER / AUTHORIZED AGENT")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div><span className={lbl}>Property name *</span><input data-testid="ban-prop-name" value={f.property.name || ""} onChange={setI("property", "name")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Property address *</span><input data-testid="ban-prop-address" value={f.property.address || ""} onChange={setI("property", "address")} className={inp} /></div>
            <div><span className={lbl}>Owner *</span><input data-testid="ban-owner" value={f.property.owner_name || ""} onChange={setI("property", "owner_name")} className={inp} /></div>
            <div><span className={lbl}>Authorized agent *</span><input data-testid="ban-agent" value={f.property.agent_name || ""} onChange={setI("property", "agent_name")} className={inp} /></div>
            <div><span className={lbl}>Agent title / phone</span><div className="flex gap-1"><input value={f.property.agent_title || ""} onChange={setI("property", "agent_title")} className={inp} /><input value={f.property.agent_phone || ""} onChange={setI("property", "agent_phone")} className={inp} /></div></div>
            <div><span className={lbl}>Authority document *</span><input data-testid="ban-authority" value={f.property.authority_document || ""} onChange={setI("property", "authority_document")} placeholder="Deed / mgmt agreement / letter" className={inp} /></div>
            <div><span className={lbl}>Subject relationship *</span>
              <select data-testid="ban-subject-rel" value={f.property.subject_relationship || ""} onChange={setI("property", "subject_relationship")} className={inp}>
                <option value="">Select…</option>{["Member of the public", "Customer", "Guest", "Employee", "Resident", "Tenant", "Contractor"].map((r) => <option key={r}>{r}</option>)}</select></div>
            <div><span className={lbl}>Lease / occupancy implications</span><input value={f.property.lease_implications || ""} onChange={setI("property", "lease_implications")} className={inp} /></div>
            <div className="col-span-3"><span className={lbl}>Excluded boundaries (blank = entire property)</span><input value={f.property.boundaries || ""} onChange={setI("property", "boundaries")} className={inp} /></div>
          </div>
          <label className="flex items-center gap-2 mt-2 font-mono text-[11px] text-[#10B981] cursor-pointer">
            <input data-testid="ban-owner-auth" type="checkbox" checked={f.owner_authorized} onChange={setT("owner_authorized")} />
            Owner / authorized-agent authorization confirmed
          </label>
        </div>

        <div className={panel}>
          <div className={ptitle}>BASIS, DURATION & EXCEPTIONS</div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3"><span className={lbl}>Basis for exclusion *</span><textarea data-testid="ban-basis" value={f.basis} onChange={setT("basis")} rows={2} className={inp} /></div>
            <div><span className={lbl}>Duration</span><input value={f.duration} onChange={setT("duration")} placeholder="e.g. 12 months" className={inp} /></div>
            <div><span className={lbl}>Expires * (date or "indefinite")</span><input data-testid="ban-expires" value={f.expires} onChange={setT("expires")} placeholder="YYYY-MM-DD or indefinite" className={inp} /></div>
            <div><span className={lbl}>Exceptions</span><input value={f.exceptions} onChange={setT("exceptions")} placeholder="e.g. court-ordered visits" className={inp} /></div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className={panel} data-testid="ban-legal-check">
          <div className={ptitle}><ShieldCheck size={11} className="inline mr-1" />OFFICER SAFETY / LEGAL CHECK</div>
          {meta.supervisor_triggers.map((t) => (
            <label key={t} className="flex items-center gap-2 py-0.5 font-mono text-[10px] text-[#C9CDD4] cursor-pointer">
              <input data-testid={`ban-trigger-${t}`} type="checkbox" checked={!!f.triggers[t]} onChange={(e) => setF((p) => ({ ...p, triggers: { ...p.triggers, [t]: e.target.checked } }))} />
              {fmt(t)}
            </label>
          ))}
          <div className="font-mono text-[9px] text-[#F59E0B] mt-1.5">Any checked item — or an indefinite exclusion — routes this notice to a supervisor before service.</div>
        </div>
        {validation && (
          <div className={panel} data-testid="ban-validation">
            <div className={ptitle}>{validation.valid ? "VALIDATION PASSED" : "MISSING ITEMS"}</div>
            {validation.valid
              ? <div className="flex items-center gap-1.5 text-[#10B981] font-mono text-[11px]"><CheckCircle2 size={13} /> Ready to generate{validation.supervisor_required ? " — supervisor review will be required" : ""}</div>
              : validation.missing.map((m) => <div key={m} className="flex items-center gap-1.5 text-[#EF4444] font-mono text-[10px]"><AlertTriangle size={11} /> {m}</div>)}
          </div>
        )}
        <div className={panel}>
          <div className="grid gap-1.5">
            <button data-testid="ban-save-draft" onClick={saveDraft} disabled={busy} className="py-2 text-[11px] font-head font-bold border border-[#1e3a5f] text-[#7d9bc1] rounded-sm">SAVE DRAFT</button>
            <button data-testid="ban-validate" onClick={validate} disabled={busy} className="py-2 text-[11px] font-head font-bold border border-[#22D3EE] text-[#22D3EE] rounded-sm">VALIDATE</button>
            <button data-testid="ban-generate" onClick={generate} disabled={busy} className="py-2 text-[11px] font-head font-bold bg-[#22D3EE] text-black rounded-sm flex items-center justify-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin" /> : null} GENERATE NOTICE / SEND FOR APPROVAL</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BanRowThumb({ nid, photo }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    setSrc(null);
    if (photo) {
      client.get(`/ban-notices/${nid}/photo`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
        .catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [nid, photo?.path]); // eslint-disable-line
  return (
    <span className="w-9 h-11 shrink-0 border border-[#1e3a5f] rounded-sm overflow-hidden bg-[#091426] flex items-center justify-center">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" data-testid={`ban-thumb-${nid}`} />
        : <UserSquare2 size={16} className="text-[#1e3a5f]" />}
    </span>
  );
}

function BanPhotoPanel({ nid, photo, isSup, onChange }) {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    let url;
    setSrc(null);
    if (photo) {
      client.get(`/ban-notices/${nid}/photo`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
        .catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [nid, photo?.path]); // eslint-disable-line
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try { await client.post(`/ban-notices/${nid}/photo`, fd); toast.success("Subject photo attached"); onChange(); }
    catch (er) { err(er, "Photo upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async () => {
    try { await client.delete(`/ban-notices/${nid}/photo`); toast.success("Photo removed"); onChange(); }
    catch (er) { err(er, "Remove failed"); }
  };
  return (
    <div className={panel} data-testid="ban-photo-panel">
      <div className={ptitle}><Camera size={11} className="inline mr-1" />SUBJECT PHOTO — OFFICER IDENTIFICATION</div>
      <div className="flex items-start gap-3">
        <div className="w-28 h-36 border border-[#1e3a5f] rounded-sm overflow-hidden bg-[#091426] flex items-center justify-center shrink-0">
          {photo
            ? (src ? <img src={src} alt="Subject" className="w-full h-full object-cover" data-testid="ban-photo-img" />
              : <Loader2 size={16} className="animate-spin text-[#22D3EE]" />)
            : <UserSquare2 size={28} className="text-[#1e3a5f]" />}
        </div>
        <div className="space-y-2">
          {photo
            ? <div className="font-mono text-[10px] text-[#7d9bc1]">Attached by {photo.uploaded_by} · {photo.ts?.slice(0, 16)}<br />Prints on the notice PDF for on-sight identification.</div>
            : <div className="font-mono text-[10px] text-[#7d9bc1]">No photo on file. Attach a photo so officers can identify the subject on sight — it also prints on the notice PDF.</div>}
          <div className="flex gap-2">
            <button data-testid="ban-photo-upload" disabled={busy} onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm disabled:opacity-50">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Camera size={11} />} {photo ? "REPLACE PHOTO" : "ATTACH PHOTO"}
            </button>
            {photo && isSup && (
              <button data-testid="ban-photo-remove" onClick={remove}
                className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-head font-bold border border-[#EF4444] text-[#EF4444] rounded-sm">
                <Trash2 size={11} /> REMOVE
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="ban-photo-input" />
        </div>
      </div>
    </div>
  );
}

function NoticeDetail({ nid, isSup, onBack }) {
  const [d, setD] = useState(null);
  const [svc, setSvc] = useState({ method: "In person — verbal & written", exact_words: "", service_location: "", subject_response: "", witness: "", signature_or_refusal: "", officer_certified: false, failed: false });
  const load = () => client.get(`/ban-notices/${nid}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [nid]); // eslint-disable-line
  if (!d) return <Loader2 className="animate-spin text-[#22D3EE]" />;
  const act = async (path, body, msg) => {
    try { await client.post(`/ban-notices/${nid}/${path}`, body); toast.success(msg); load(); }
    catch (e) { err(e, "Action failed"); }
  };
  const pdf = async () => {
    try {
      const r = await client.get(`/ban-notices/${nid}/notice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `${d.notice_number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { err(e, "PDF failed"); }
  };
  const svcSet = (k) => (e) => setSvc((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const doService = (failed) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => act("service", { ...svc, failed, gps_lat: pos.coords.latitude, gps_lng: pos.coords.longitude }, failed ? "Attempt recorded" : "Service recorded"),
        () => act("service", { ...svc, failed }, failed ? "Attempt recorded" : "Service recorded"));
    } else act("service", { ...svc, failed }, "Recorded");
  };
  return (
    <div data-testid="ban-detail" className="max-w-4xl space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="font-mono text-[11px] text-[#7d9bc1] hover:text-white">← Back</button>
        <span className="font-head font-bold text-white">{d.notice_number || "DRAFT"} — {d.subject.name}</span>
        <span className="font-mono text-[10px] font-bold px-2 py-0.5 border rounded-sm" style={{ color: STATUS_COLOR[d.status], borderColor: `${STATUS_COLOR[d.status]}55` }} data-testid="ban-detail-status">{fmt(d.status)}</span>
        {d.notice_number && <button data-testid="ban-pdf" onClick={pdf} className="ml-auto flex items-center gap-1 px-2.5 py-1 text-[10px] font-head font-bold bg-[#E0B23C] text-black rounded-sm"><Printer size={11} /> PRINT (3 COPIES)</button>}
      </div>
      <div className={panel + " grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-white"}>
        {[["CAD #", d.incident.cad_number], ["Call type", d.incident.call_type], ["Property", d.property.name], ["Owner/Agent", d.property.agent_name || d.property.owner_name],
          ["Expires", d.expires], ["Verify code", d.verify_code || "—"], ["Created by", d.created_by], ["Served", d.served_at?.slice(0, 16) || "—"]].map(([k, v]) => (
          <div key={k}><div className="font-mono text-[9px] text-[#7d9bc1] uppercase">{k}</div><div className="font-semibold truncate">{v || "—"}</div></div>
        ))}
      </div>
      <BanPhotoPanel nid={nid} photo={d.photo} isSup={isSup} onChange={load} />
      {d.status === "pending_supervisor_review" && isSup && (
        <div className={panel} data-testid="ban-review-panel">
          <div className={ptitle}>SUPERVISOR REVIEW REQUIRED</div>
          <div className="flex gap-2">
            <button data-testid="ban-approve" onClick={() => act("review", { approve: true }, "Approved for service")} className="px-3 py-1.5 text-[11px] font-head font-bold bg-[#10B981] text-black rounded-sm">APPROVE FOR SERVICE</button>
            <button data-testid="ban-return" onClick={() => act("review", { approve: false, note: "Returned for correction" }, "Returned to draft")} className="px-3 py-1.5 text-[11px] font-head font-bold border border-[#EF4444] text-[#EF4444] rounded-sm">RETURN</button>
          </div>
        </div>
      )}
      {(d.status === "approved_for_service" || d.status === "service_attempted") && (
        <div className={panel} data-testid="ban-service-panel">
          <div className={ptitle}>STEP 5 — SERVICE DOCUMENTATION</div>
          <div className="grid grid-cols-2 gap-2">
            <div><span className={lbl}>Method *</span><select value={svc.method} onChange={svcSet("method")} className={inp}>{["In person — verbal & written", "In person — verbal only", "Posted at property", "Certified mail", "Via attorney/agent"].map((m) => <option key={m}>{m}</option>)}</select></div>
            <div><span className={lbl}>Service location *</span><input data-testid="ban-svc-location" value={svc.service_location} onChange={svcSet("service_location")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Exact words communicated *</span><input data-testid="ban-svc-words" value={svc.exact_words} onChange={svcSet("exact_words")} className={inp} /></div>
            <div><span className={lbl}>Subject response</span><input value={svc.subject_response} onChange={svcSet("subject_response")} className={inp} /></div>
            <div><span className={lbl}>Witness</span><input value={svc.witness} onChange={svcSet("witness")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Signature or refusal</span><input value={svc.signature_or_refusal} onChange={svcSet("signature_or_refusal")} className={inp} /></div>
          </div>
          <label className="flex items-center gap-2 mt-2 font-mono text-[11px] text-[#10B981] cursor-pointer">
            <input data-testid="ban-svc-certify" type="checkbox" checked={svc.officer_certified} onChange={svcSet("officer_certified")} />
            I certify this service record is true and accurate (GPS position will be captured)
          </label>
          <div className="flex gap-2 mt-2">
            <button data-testid="ban-svc-served" onClick={() => doService(false)} className="px-3 py-1.5 text-[11px] font-head font-bold bg-[#10B981] text-black rounded-sm">RECORD SERVICE — SERVED</button>
            <button data-testid="ban-svc-failed" onClick={() => doService(true)} className="px-3 py-1.5 text-[11px] font-head font-bold border border-[#F97316] text-[#F97316] rounded-sm">FAILED ATTEMPT</button>
          </div>
        </div>
      )}
      {d.status === "active" && (
        <div className="grid md:grid-cols-2 gap-3">
          <div className={panel} data-testid="ban-violation-panel">
            <div className={ptitle}><FileWarning size={11} className="inline mr-1" />VIOLATION REPORT</div>
            <ViolationForm onSubmit={(body) => act("violation", body, "Violation documented")} />
          </div>
          {isSup && (
            <div className={panel} data-testid="ban-revoke-panel">
              <div className={ptitle}><Undo2 size={11} className="inline mr-1" />REVOCATION</div>
              <RevokeForm onSubmit={(body) => act("revoke", body, "Revocation recorded")} />
            </div>
          )}
        </div>
      )}
      <div className={panel}>
        <div className={ptitle}>SUPPLEMENTS (original is immutable after service)</div>
        {(d.supplements || []).map((s, i) => <div key={i} className="font-mono text-[10px] text-[#C9CDD4] py-0.5">{s.ts.slice(0, 16)} · {s.by}: {s.text}</div>)}
        <SupplementForm onSubmit={(t) => act("supplement", { text: t }, "Supplement recorded")} />
      </div>
      <div className={panel}>
        <div className={ptitle}>AUDIT TRAIL</div>
        <div className="max-h-40 overflow-y-auto">
          {(d.audit || []).map((a, i) => <div key={i} className="font-mono text-[9px] text-[#7d9bc1] py-px">{a.ts.slice(0, 19)} · {a.action.toUpperCase()} · {a.by} ({a.role}) {a.detail && `— ${a.detail}`}</div>)}
        </div>
      </div>
    </div>
  );
}

function SupplementForm({ onSubmit }) {
  const [t, setT] = useState("");
  return (
    <div className="flex gap-1.5 mt-1.5">
      <input data-testid="ban-supplement-text" value={t} onChange={(e) => setT(e.target.value)} placeholder="Timestamped correction / addition…" className={inp} />
      <button data-testid="ban-supplement-add" onClick={() => { if (t.trim()) { onSubmit(t); setT(""); } }} className="px-3 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm shrink-0">ADD</button>
    </div>
  );
}

function ViolationForm({ onSubmit }) {
  const [f, setF] = useState({ return_location: "", within_boundary: "Yes", reason_for_return: "", owner_confirmation: "", action_taken: "" });
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="space-y-1.5">
      <input data-testid="ban-vio-location" value={f.return_location} onChange={s("return_location")} placeholder="Exact return location *" className={inp} />
      <div className="flex gap-1.5">
        <select value={f.within_boundary} onChange={s("within_boundary")} className={inp}><option>Yes</option><option>No</option><option>Unclear</option></select>
        <input value={f.reason_for_return} onChange={s("reason_for_return")} placeholder="Reason for return" className={inp} />
      </div>
      <input value={f.owner_confirmation} onChange={s("owner_confirmation")} placeholder="Current owner/agent confirmation" className={inp} />
      <input value={f.action_taken} onChange={s("action_taken")} placeholder="Action taken (warn/cite/arrest/refer)" className={inp} />
      <button data-testid="ban-vio-submit" onClick={() => f.return_location && onSubmit(f)} className="px-3 py-1.5 text-[10px] font-head font-bold bg-[#EF4444] text-white rounded-sm">FILE VIOLATION REPORT</button>
    </div>
  );
}

function RevokeForm({ onSubmit }) {
  const [f, setF] = useState({ type: "full", reason: "", agent_signature: "" });
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="space-y-1.5">
      <select data-testid="ban-revoke-type" value={f.type} onChange={s("type")} className={inp}>
        <option value="full">Fully revoked</option><option value="suspend">Temporarily suspended</option>
        <option value="boundaries">Property boundaries modified</option><option value="exceptions">Exceptions added</option>
        <option value="duration">Duration modified</option><option value="replaced">Replaced by a new notice</option>
      </select>
      <input value={f.reason} onChange={s("reason")} placeholder="Reason *" className={inp} />
      <input value={f.agent_signature} onChange={s("agent_signature")} placeholder="Authorized agent signature (typed)" className={inp} />
      <button data-testid="ban-revoke-submit" onClick={() => f.reason && onSubmit(f)} className="px-3 py-1.5 text-[10px] font-head font-bold border border-[#EF4444] text-[#EF4444] rounded-sm">RECORD REVOCATION</button>
    </div>
  );
}
